import { useState, useEffect, useContext, Fragment, useCallback } from "react"
import { AuthContext } from "../context/AuthContext"
import OrderSummaryModal from "../components/OrderSummaryModal"
import axios from "axios"
import {
  Plus, Trash2, User, Phone, MapPin,
  Truck, ShoppingBag, CreditCard, CheckCircle,
  Package, Globe, Check, ChevronsUpDown
} from "lucide-react"
import { Combobox, Transition } from '@headlessui/react'
import { showToast } from "../utils/toastUtils"

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

const CreateOrder = () => {
  const { token } = useContext(AuthContext)
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [showOrderSummary, setShowOrderSummary] = useState(false)
  const [orderData, setOrderData] = useState(null)
  const [couriers, setCouriers] = useState([])
  const [selectedCourier, setSelectedCourier] = useState("")
  const [globalThreshold, setGlobalThreshold] = useState(2000)

  // Cities data
  const [citiesData, setCitiesData] = useState([])
  const [availableDistricts, setAvailableDistricts] = useState([])
  const [availableCities, setAvailableCities] = useState([])

  // Customer details
  const [customerDetails, setCustomerDetails] = useState({
    customerName: "",
    addressLine1: "",
    addressLine2: "",
    addressLine3: "",
    city: "",
    zipCode: "",
    phone: "",
    phone2: "",
    orderBy: "Facebook",
    contactDetail: "",
    province: "",
  })

  // Order lines
  const [orderLines, setOrderLines] = useState([
    {
      id: 1,
      productId: "",
      productName: "",
      variation: "",
      quantity: 1,
      mrp: 0,
      lineTotal: 0,
      query: "" // Added for search state
    },
  ])

  // Order summary
  const [orderSummary, setOrderSummary] = useState({
    subTotal: 0,
    shippingCost: 0,
    total: 0,
  })

  // Reusable Input Style
  const inputStyle = "w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 text-gray-700 placeholder-gray-400 shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none hover:border-blue-300"
  const selectStyle = "w-full rounded-lg border-gray-200 bg-white px-4 py-2.5 text-gray-700 shadow-sm transition-all duration-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 focus:outline-none hover:border-blue-300"

  if (token) {
    axios.defaults.headers.common["x-auth-token"] = token
  }

  // Fetch products & couriers
  useEffect(() => {
    const fetchProductsAndCouriers = async () => {
      try {
        setLoading(true)
        const [productRes, courierRes, cityRes, settingsRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/products`),
          axios.get(`${BASE_URL}/api/couriers`),
          axios.get(`${BASE_URL}/api/cities`),
          axios.get(`${BASE_URL}/api/settings/freeDeliveryThreshold`).catch(() => null)
        ])
        setProducts(productRes.data)
        setCouriers(courierRes.data)
        if (settingsRes && settingsRes.data && settingsRes.data.value) {
          setGlobalThreshold(parseFloat(settingsRes.data.value))
        }

        const cities = cityRes.data || []
        setCitiesData(cities)

        // Extract unique districts from cities data, sorting them alphabetically
        const uniqueDistricts = [...new Set(cities.map(c => c.district_name))]
          .filter(Boolean)
          .sort()
        setAvailableDistricts(uniqueDistricts)

      } catch (err) {
        console.error("Error fetching data:", err)
        showToast("Error", err.response?.data?.message || "Error fetching data", "error")
      } finally {
        setLoading(false)
      }
    }
    fetchProductsAndCouriers()
  }, [token])

  const calculateSubTotal = () => orderLines.reduce((acc, line) => acc + (line.lineTotal || 0), 0)

  const calculateShippingCost = (subTotal, province) => {
    if (!province || !selectedCourier) return 0
    if (subTotal >= globalThreshold) return 0 // Free shipping threshold

    const courier = couriers.find(c => c._id === selectedCourier)
    if (!courier || !courier.fees || !courier.fees[province]) return 0
    return courier.fees[province]
  }

  const updateOrderSummary = useCallback(() => {
    const subTotal = calculateSubTotal()
    const shippingCost = calculateShippingCost(subTotal, customerDetails.province)
    setOrderSummary({
      subTotal,
      shippingCost,
      total: subTotal + shippingCost, // Logic: subTotal + shipping (shipping logic handles free/not free)
    })
  }, [customerDetails.province, orderLines, selectedCourier, couriers])

  // Update summary when lines or shipping details change
  useEffect(() => {
    updateOrderSummary()
  }, [orderLines, customerDetails.province, selectedCourier, updateOrderSummary])

  const handleCustomerChange = (e) => {
    const { name, value } = e.target

    if (name === "province") {
      // If district changes, reset the city and filter available cities
      const filteredCities = citiesData
        .filter(c => c.district_name === value)
        .map(c => c.city_name)
        .filter(Boolean)
        .sort()

      setAvailableCities(filteredCities)
      setCustomerDetails({ ...customerDetails, province: value, city: "", zipCode: "" })
      updateOrderSummary()
    } else if (name === "city") {
      // If city changes, auto-fill postal code
      const selectedCityData = citiesData.find(
        c => c.city_name === value && c.district_name === customerDetails.province
      )

      setCustomerDetails({
        ...customerDetails,
        city: value,
        zipCode: selectedCityData?.postcode || ""
      })
    } else {
      setCustomerDetails({ ...customerDetails, [name]: value })
    }
  }

  const handleOrderLineChange = (index, field, value) => {
    const updatedOrderLines = [...orderLines]

    if (field === "productId") {
      // Allow clearing selection
      if (!value) {
        updatedOrderLines[index] = {
          ...updatedOrderLines[index],
          productId: "",
          productName: "",
          variation: "",
          mrp: 0,
          lineTotal: 0,
          query: "" // Reset query on clear if needed, but usually we keep it or handle in Combobox
        }
      } else {
        // Value is the Product Object from Combobox
        const selectedProduct = value
        if (selectedProduct) {
          const quantity = updatedOrderLines[index].quantity || 1
          const mrp = selectedProduct.mrp || 0
          updatedOrderLines[index] = {
            ...updatedOrderLines[index],
            productId: selectedProduct._id,
            productName: `[${selectedProduct.productType}] ${selectedProduct.productName}`,
            variation: selectedProduct.variation,
            mrp,
            lineTotal: quantity * mrp,
            query: selectedProduct.productName // Set query to name for display
          }
        }
      }
    } else if (field === "quantity") {
      const quantity = Math.max(1, Number.parseInt(value) || 1)
      updatedOrderLines[index] = {
        ...updatedOrderLines[index],
        quantity,
        lineTotal: quantity * (updatedOrderLines[index].mrp || 0),
      }
    } else if (field === "variation") {
      updatedOrderLines[index].variation = value
    } else if (field === "query") {
      updatedOrderLines[index].query = value
    }

    setOrderLines(updatedOrderLines)
  }

  const addOrderLine = () => {
    setOrderLines([
      ...orderLines,
      {
        id: Date.now(),
        productId: "",
        productName: "",
        variation: "",
        quantity: 1,
        mrp: 0,
        lineTotal: 0,
        query: ""
      }
    ])
  }

  const removeOrderLine = (index) => {
    if (orderLines.length > 1) {
      setOrderLines(orderLines.filter((_, i) => i !== index))
    }
  }

  const showOrderSummaryModal = () => {
    setOrderData({ customerDetails, orderLines, orderSummary, courierThreshold: globalThreshold })
    setShowOrderSummary(true)
  }

  const validateFormData = () => {
    if (!customerDetails.province) {
      showToast("Validation Error", "Please select a District", "error")
      return false
    }
    const required = ["customerName", "addressLine1", "city", "phone", "contactDetail"]
    for (const field of required) {
      if (!customerDetails[field]) {
        showToast("Validation Error", `Please fill in ${field.replace(/([A-Z])/g, ' $1').trim()}`, "error")
        return false
      }
    }

    if (calculateSubTotal() <= 0) {
      showToast("Validation Error", "Please add at least one product", "error")
      return false
    }

    if (orderLines.some(l => !l.productId || l.quantity < 1)) {
      showToast("Validation Error", "Please complete all product details", "error")
      return false
    }

    return true
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateFormData()) return

    try {
      setLoading(true)
      const payload = {
        ...customerDetails,
        courierServiceId: selectedCourier,
        orderLines: orderLines.map(l => ({
          productId: l.productId,
          variation: l.variation,
          quantity: l.quantity,
          lineTotal: l.lineTotal
        })),
        ...orderSummary
      }

      await axios.post(`${BASE_URL}/api/orders`, payload)
      showToast("Success", "Order created successfully!", "success")
      showOrderSummaryModal()

      // Reset
      setCustomerDetails(prev => ({
        ...prev,
        customerName: "", addressLine1: "", addressLine2: "", addressLine3: "",
        city: "", zipCode: "", phone: "", phone2: "", contactDetail: "", province: ""
      }))
      setOrderLines([{ id: Date.now(), productId: "", productName: "", variation: "", quantity: 1, mrp: 0, lineTotal: 0, query: "" }])
    } catch (err) {
      console.error(err)
      showToast("Error", err.response?.data?.message || "Failed to create order", "error")
    } finally {
      setLoading(false)
    }
  }

  // Hardcoded districts removed in favor of dynamic loading from cities data.

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Package className="w-6 h-6 md:w-8 md:h-8 text-blue-600" /> Create New Order
          </h1>
          <p className="mt-2 text-sm text-gray-500">Enter customer details and add products to generate a new order.</p>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">

          {/* Left Column: Customer Details */}
          <div className="lg:col-span-2 space-y-6">

            {/* Customer Info Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-500" /> Customer Information
              </h2>
              <div className="grid grid-cols-1 gap-4 md:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                  <input type="text" name="customerName" value={customerDetails.customerName} onChange={handleCustomerChange}
                    className={inputStyle}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input type="text" name="phone" value={customerDetails.phone} onChange={handleCustomerChange}
                      className={`${inputStyle} pl-10`}
                      placeholder="0771234567"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alt Phone</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input type="text" name="phone2" value={customerDetails.phone2} onChange={handleCustomerChange}
                      className={`${inputStyle} pl-10`}
                      placeholder="Optional"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Address & Delivery Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-green-500" /> Delivery Details
              </h2>
              <div className="grid grid-cols-1 gap-4 md:gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1 <span className="text-red-500">*</span></label>
                  <input type="text" name="addressLine1" value={customerDetails.addressLine1} onChange={handleCustomerChange}
                    className={inputStyle}
                    placeholder="House No, Street Name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                  <input type="text" name="addressLine2" value={customerDetails.addressLine2} onChange={handleCustomerChange}
                    className={inputStyle}
                    placeholder="Apartment, Suite (Optional)"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">District <span className="text-red-500">*</span></label>
                    <select name="province" value={customerDetails.province} onChange={handleCustomerChange}
                      className={selectStyle}
                    >
                      <option value="">Select District</option>
                      {availableDistricts.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City <span className="text-red-500">*</span></label>
                    <select name="city" value={customerDetails.city} onChange={handleCustomerChange}
                      className={selectStyle}
                      disabled={!customerDetails.province}
                    >
                      <option value="">{customerDetails.province ? "Select City" : "Select District First"}</option>
                      {availableCities.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code <span className="text-red-500">*</span></label>
                    <input type="text" name="zipCode" value={customerDetails.zipCode} onChange={handleCustomerChange}
                      className={inputStyle}
                    />
                  </div>
                </div>

                <div className="border-t border-dashed border-gray-200 my-2"></div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Courier Service</label>
                  <div className="relative">
                    <Truck className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <select value={selectedCourier} onChange={(e) => setSelectedCourier(e.target.value)}
                      className={`${selectStyle} pl-10`}
                    >
                      <option value="">Select Courier</option>
                      {couriers.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order Source</label>
                  <div className="flex flex-wrap gap-4 mt-2">
                    {["Facebook", "WhatsApp", "Phone"].map((method) => (
                      <label key={method} className="flex items-center cursor-pointer">
                        <input type="radio" name="orderBy" value={method} checked={customerDetails.orderBy === method} onChange={handleCustomerChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">{method}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {customerDetails.orderBy === "Facebook" ? "Facebook Profile URL" : customerDetails.orderBy === "WhatsApp" ? "WhatsApp Number" : "Source Details"}
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <input type="text" name="contactDetail" value={customerDetails.contactDetail} onChange={handleCustomerChange}
                      className={`${inputStyle} pl-10`}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Products Card - Mobile (Hidden) */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 lg:hidden">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-purple-500" /> Order Items
              </h2>
              {/* Note: In previous code this was empty/hidden to possibly suppress duplication? but logic below is cleaner. Keeping as is if logic requires it, but usually standard flow is better. 
                  Actually, the original code had this block but it was empty/just header? 
                  Ah, I see content below. I will replicate content logic here or let it flow.
                  The original code had "Order Items & Summary" in the Right Column. 
                  On Mobile, Right Column drops below Left Column.
                  So we DO need Order Items to appear.
                  The original code had `Order Items` in the right column only. 
                  Users usually want to add items AFTER customer details. 
                  So `lg:col-span-1` drops to bottom. This is fine. 
                  I will remove this redundant hidden block if it doesn't serve a purpose.
              */}
            </div>

          </div>

          {/* Right Column: Order Items & Summary (Sticky) */}
          <div className="lg:col-span-1 space-y-6">

            {/* Order Items Card */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-purple-500" /> Order Items
              </h2>

              <div className="space-y-4">
                {orderLines.map((line, index) => {
                  // Filter products based on search query for this line
                  const filteredProducts =
                    line.query === ''
                      ? products
                      : products.filter((product) => {
                        return (
                          product.productName.toLowerCase().includes(line.query.toLowerCase()) ||
                          product.productType.toLowerCase().includes(line.query.toLowerCase())
                        )
                      })

                  return (
                    <div key={line.id} className="p-3 md:p-4 bg-gray-50 rounded-lg border border-gray-200 relative group">
                      <button type="button" onClick={() => removeOrderLine(index)}
                        className="absolute top-2 right-2 text-gray-400 hover:text-red-500 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>

                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase">Product</label>

                          {/* Headless UI Combobox */}
                          <Combobox
                            value={products.find(p => p._id === line.productId) || null}
                            onChange={(val) => handleOrderLineChange(index, "productId", val)}
                          >
                            <div className="relative mt-1">
                              <div className="relative w-full cursor-default overflow-hidden rounded-lg bg-white text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm">
                                <Combobox.Input
                                  className={inputStyle}
                                  displayValue={(product) => product ? displayProductName(product) : ''}
                                  onChange={(event) => handleOrderLineChange(index, "query", event.target.value)}
                                  placeholder="Search product..."
                                />
                                <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronsUpDown
                                    className="h-5 w-5 text-gray-400"
                                    aria-hidden="true"
                                  />
                                </Combobox.Button>
                              </div>
                              <Transition
                                as={Fragment}
                                leave="transition ease-in duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                                afterLeave={() => handleOrderLineChange(index, "query", "")}
                              >
                                <Combobox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-50">
                                  {filteredProducts.length === 0 && line.query !== "" ? (
                                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                                      Nothing found.
                                    </div>
                                  ) : (
                                    filteredProducts.map((product) => (
                                      <Combobox.Option
                                        key={product._id}
                                        className={({ active }) =>
                                          `relative cursor-default select-none py-2 pl-10 pr-4 ${active ? 'bg-blue-600 text-white' : 'text-gray-900'
                                          }`
                                        }
                                        value={product}
                                      >
                                        {({ selected, active }) => (
                                          <>
                                            <span
                                              className={`block truncate ${selected ? 'font-medium' : 'font-normal'
                                                }`}
                                            >
                                              {/* Custom Option Display */}
                                              {displayProductName(product)}
                                            </span>
                                            {selected ? (
                                              <span
                                                className={`absolute inset-y-0 left-0 flex items-center pl-3 ${active ? 'text-white' : 'text-blue-600'
                                                  }`}
                                              >
                                                <Check className="h-5 w-5" aria-hidden="true" />
                                              </span>
                                            ) : null}
                                          </>
                                        )}
                                      </Combobox.Option>
                                    ))
                                  )}
                                </Combobox.Options>
                              </Transition>
                            </div>
                          </Combobox>

                        </div>

                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase">Qty</label>
                            <input type="number" min="1" value={line.quantity} onChange={(e) => handleOrderLineChange(index, "quantity", e.target.value)}
                              className={`${inputStyle} mt-1`}
                            />
                          </div>
                          <div className="col-span-2">
                            <label className="text-xs font-semibold text-gray-500 uppercase">Line Total</label>
                            <div className="mt-1 text-sm font-mono font-medium text-gray-700 bg-white border border-gray-200 px-3 py-2 rounded-md">
                              Rs. {(line.lineTotal || 0).toFixed(2)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })}

                <button type="button" onClick={addOrderLine}
                  className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:border-blue-500 hover:text-blue-500 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add Another Item
                </button>
              </div>
            </div>

            {/* Order Summary (Sticky) */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-4 md:p-6 lg:sticky lg:top-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-amber-500" /> Order Summary
              </h2>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between items-center text-gray-600">
                  <span>Subtotal</span>
                  <span className="font-medium text-gray-900">Rs. {orderSummary.subTotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between items-center text-gray-600">
                  <span>Shipping ({customerDetails.province || "None"})</span>
                  <span className="font-medium text-gray-900">Rs. {orderSummary.shippingCost.toFixed(2)}</span>
                </div>

                {orderSummary.subTotal >= globalThreshold && (
                  <div className="bg-green-50 text-green-700 px-3 py-2 rounded-md text-xs flex items-center gap-2">
                    <CheckCircle className="w-3 h-3" /> Free shipping applied!
                  </div>
                )}

                <div className="border-t border-gray-200 pt-3 mt-3">
                  <div className="flex justify-between items-center text-lg font-bold text-gray-900">
                    <span>Total</span>
                    <span>Rs. {orderSummary.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              <button type="submit" disabled={loading}
                className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-lg shadow-md transition-all transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> Processing...</>
                ) : (
                  <><CheckCircle className="w-5 h-5" /> Confirm Order</>
                )}
              </button>
            </div>

          </div>

        </form>
      </div>



      {orderData && (
        <OrderSummaryModal isOpen={showOrderSummary} onClose={() => setShowOrderSummary(false)} orderData={orderData} />
      )}
    </div>
  )
}

// Helper to display product name consistently
const displayProductName = (p) => {
  if (!p) return "";
  return `${p.productName} - ${p.variation} [${p.productType}] (Rs.${p.mrp})`
}

export default CreateOrder
